import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { INNER_CELL, TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { holeIdsLabel, type HoleGroup } from './holeGroups';
import { makesOwnHole, shownHoleId } from './stepHoles';
import type { CoverLine, JointCheck } from './jointCheck';
import { namedIn, withLetter } from './partsCatalogue';
import { sidedName, symmetryOf, type PartItem } from './partsRun';
import { segCls } from './PartSelector';
import type { EntrySide, HardwareInteraction } from './hardwareInteractionsRun';
import { HolesGrid, holeRows, holesOf, perHole, twiceIdsOf, type HoleRow } from './HolesGrid';
import { drillingCells } from './DrillingTable';
import { useHoleWords } from './HolesTable';
import { useDragScroll } from './useDragScroll';
import type { ByPart } from './partModelSize';
import { useLanguage } from '../../contexts/LanguageContext';

// THE JOINT VIEW'S TWO READINGS: which joints are right (the list), and, for the one open, each
// hole the hardware table expects against what Find holes found (the lines). See jointCheck.

// ONE JOINT CARD'S WIDTH, and the gap between two.
const CARD_PX = 176;
const CARD_GAP_PX = 8;

// Where a joint stands: being asked, waiting its turn in a run, never asked, or checked with this
// many things wrong.
export type JointStatus = { state: 'asking' | 'pending' | 'notRun' | 'checked'; issues: number };

// THE JOINTS, ONE LINE EACH, in the run menu's order and under its names — a checklist: a tick
// where every expected hole is there, the number of things to look at where not.
export function JointList({ groups, at, status, onSelect, whole, first, items }: {
  groups: HoleGroup[];
  // For the P of a joint whose screws make their own holes (see shownHoleId).
  items?: PartItem[];
  at: number;
  status: JointStatus[];
  onSelect: (k: number) => void;
  // THE ASSEMBLED PRODUCT, last: every joint at once.
  whole: { on: boolean; status: JointStatus; onSelect: () => void };
  // A TAB BEFORE THE JOINTS, about none of them and so with no status — the Drilling table.
  first?: { label: string; on: boolean; onSelect: () => void };
}) {
  const { t } = useLanguage();
  // EVERY JOINT IN SIGHT, EACH CARD ALIKE (one width, the tallest one's height), never a bar to
  // scroll — AND THE ROWS AS EVEN AS THEY CAN BE: as few rows as the width allows, the cards shared
  // out evenly over them, and a last row with fewer centred under the others. A card spans two
  // half-columns, so a row one card short starts half a card in.
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const seen = new ResizeObserver(() => setWidth(el.clientWidth));
    seen.observe(el);
    return () => seen.disconnect();
  }, []);
  const lead = first ? 1 : 0;
  const n = groups.length + 1 + lead;
  const fit = Math.max(1, Math.floor((width + CARD_GAP_PX) / (CARD_PX + CARD_GAP_PX)));
  const per = Math.ceil(n / Math.ceil(n / fit));
  const last = n - per * (Math.ceil(n / per) - 1);
  const placed = (k: number): CSSProperties => ({
    gridColumn: `${k === n - last && last < per ? per - last + 1 : 'auto'} / span 2`,
  });
  const entry = (k: number, key: string, label: ReactNode, s: JointStatus | null, on: boolean, select: () => void, full?: string) => (
    <button
      key={key}
      type="button"
      onClick={select}
      title={full}
      aria-pressed={on}
      style={placed(k)}
      // PartSelector's tile, chosen or not: one look for every strip of tabs.
      className={`${segCls(on)} text-center`}
    >
      <span className="min-w-0 flex-1 text-xs font-semibold">{label}</span>
      {s && <StatusMark s={s} on={on} />}
    </button>
  );
  return (
    <div ref={box} className="w-full">
      <div
        className="grid auto-rows-fr justify-center"
        style={{ gap: CARD_GAP_PX, gridTemplateColumns: `repeat(${per * 2}, ${(CARD_PX - CARD_GAP_PX) / 2}px)` }}
      >
        {first && entry(0, 'first', first.label, null, first.on, first.onSelect)}
        {/* THE JOINT'S HOLE IDS OVER ITS NAME — what ties it to the Drilling table's rows. */}
        {groups.map((g, k) => {
          const ids = holeIdsLabel(g, !!items && g.rows.length > 0
            && g.rows.every((r) => r.hardware.some((h) => makesOwnHole(items[Number(h)]))));
          const label = ids ? <><span className="mb-1 block">{ids}</span><span className="block">{g.parts}</span></> : g.parts;
          return entry(k + lead, `${g.i}#${g.g}`, label, status[k], k === at, () => onSelect(k), g.what);
        })}
        {entry(n - 1, 'assembled', t('notice.joints.assembled'), whole.status, whole.on, whole.onSelect)}
      </div>
    </div>
  );
}

// WHERE A JOINT STANDS, in one mark: a spinner, a dash for never run, the number of things to
// look at, or a tick — nothing for never run. `on`: drawn on the chosen entry's blue.
function StatusMark({ s, on = false }: { s: JointStatus; on?: boolean }) {
  const { t } = useLanguage();
  if (s.state === 'asking') return <Loader2 className="h-4 w-4 shrink-0 animate-spin" />;
  if (s.state === 'pending') {
    return <span className="shrink-0 text-[11px] font-medium opacity-70">{t('notice.joints.pending')}</span>;
  }
  // NEVER RUN: no mark at all — a dash beside the name read as one more thing to look at.
  if (s.state === 'notRun') {
    return null;
  }
  if (s.issues) {
    return (
      <span
        title={t('notice.joints.toCheck').replace('{n}', String(s.issues))}
        className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-semibold tabular-nums text-white"
      >
        {s.issues}
      </span>
    );
  }
  return <Check className={`h-4 w-4 shrink-0 ${on ? '' : 'text-blue-600'}`} />;
}

// THE JOINT'S ROWS OF THE DRILLING TABLE, and what Drilling holes made of each: where its first hole
// was placed, how deep each hole measured on the meshes, and whether it is right. A row is the switch
// that draws its holes on the model, or hides them; a part is the link to its own view, where it is
// drilled. Then what was found that no line expects.
export function ExpectedLines({
  rows, sizes, check, covers, coverNotes, items, picked, onPick, pickedCovers, onPickCover, onOpenPart, notes, partNotes,
}: {
  // The joint's Joints-table rows — the Drilling table's rows for it are read off them.
  rows: HardwareInteraction[];
  // Each part's model size and Insertion answer: the Diameter and Depth columns.
  sizes: ByPart;
  check: JointCheck;
  // The covers the joint's rows name, against what was placed — see checkCovers.
  covers: CoverLine[];
  // What the covers call said it could not place.
  coverNotes: string;
  // The covers whose rows are on — outlined on the model — by cover part; a row is the switch.
  pickedCovers: ReadonlySet<string>;
  onPickCover: (cover: string) => void;
  items: PartItem[];
  // The holes drawn, by id.
  picked: ReadonlySet<string>;
  onPick: (key: string) => void;
  onOpenPart: (part: string) => void;
  // "H103 · …", each under the line holding its hole — a hole's notes together, in one row.
  notes: string[];
  // About a part as a whole, not one of its holes: the table's last rows.
  partNotes: string[];
}) {
  const { t, language } = useLanguage();
  const th = TABLE_HEAD;
  const td = TABLE_CELL;
  const name = (id: string, side: EntrySide) => {
    const p = items[Number(id)];
    return sidedName(namedIn(p, language) || `#${id}`, symmetryOf(p) === 'left-right' ? side : null, language);
  };
  const { axis } = useHoleWords();
  const scrollRef = useDragScroll<HTMLDivElement>();
  const noteId = (n: string) => n.split(' · ')[0];
  // THE DRILLING TABLE'S ROWS FOR THIS JOINT, and every hole Drilling holes found, by id.
  const grid = holeRows(rows);
  const found = new Map(check.lines.flatMap((l) => l.holes).map((h) => [h.id, h]));
  const foundOf = (id: string | undefined) => (id ? found.get(id) : undefined);
  const shown = new Set(grid.flatMap((r) => holesOf(r).map((x) => x.id)).filter(Boolean));
  const loose = notes.filter((n) => !shown.has(noteId(n)));
  // A ROW'S SWITCHES: its own holes, by id — so each run is drawn on its own.
  const keysOf = (r: HoleRow) => holesOf(r).flatMap(({ id }) => (id ? [id] : []));
  const amber = 'text-amber-600';
  const partLink = (id: string, label: string) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpenPart(id); }}
      title={t('notice.joints.openPart')}
      className="text-blue-600 underline-offset-2 hover:underline"
    >
      {label}
    </button>
  );

  return (
    <TableCard title={t('notice.joints.expected')}>
      {grid.length ? (
        // DRAGGED SIDEWAYS as well as scrolled, as the Joints table is — and centred inside the
        // scroller, never by it: a centred row wider than its box is cut on the left, out of reach.
        // NO WIDER THAN THE CARD: the Status column wraps to fit (below), so the scroller is only
        // ever wanted on a screen too narrow for the other columns themselves.
        <div ref={scrollRef} className="w-full overflow-x-auto text-xs text-slate-700">
          <div className="mx-auto w-max max-w-full">
          <HolesGrid
            rows={grid}
            items={items}
            twiceIds={twiceIdsOf(rows.flatMap((r) => r.holes))}
            onRow={(r) => keysOf(r).forEach(onPick)}
            isOn={(r) => keysOf(r).length > 0 && keysOf(r).every((k) => picked.has(k))}
            partLink={partLink}
            extra={{
              heads: [t('notice.drilling.diameter'), t('notice.drilling.depth'), t('notice.joints.position'),
                t('notice.joints.measured'), t('notice.joints.foundCol')],
              cells: (r) => (
                <>
                  {drillingCells(r, sizes, t)}
                  {/* WHERE ITS FIRST HOLE WAS PLACED — the one thing Drilling holes is asked. */}
                  <td className={`${INNER_CELL} whitespace-nowrap`}>
                    {(() => {
                      const h = foundOf(r.ids[0]);
                      return h ? <>{axis(h.u)},<br />{axis(h.v)}</> : '';
                    })()}
                  </td>
                  {/* HOW DEEP EACH HOLE MEASURED on the meshes (see holesOfLine). */}
                  <td className={INNER_CELL}>
                    {perHole(r, (_h, id) => {
                      const h = foundOf(id);
                      if (!h) return '';
                      return [h.through ? t('notice.holes.through') : h.depthMm != null
                        ? t('notice.joints.depthDeep').replace('{mm}', String(h.depthMm)) : '',
                      h.sunkMm ? t('notice.holes.sunk').replace('{n}', String(h.sunkMm)) : '']
                        .filter(Boolean).join(' · ');
                    })}
                  </td>
                  {/* RIGHT, OR WHAT IS NOT: a line per hole, by its id — "H101-A : ✓" — and a line per
                      thing wrong with it, never two run together. The one column that WRAPS: its
                      messages are sentences, and kept on one line they pushed the table past the card. */}
                  <td className={`${INNER_CELL} min-w-56 whitespace-normal`}>
                    {holesOf(r).flatMap(({ h: line, id }) => {
                      const h = foundOf(id);
                      const lc = check.lines.find((l) => l.line === line && l.side === r.side);
                      const said = [
                        !h && t('notice.joints.notPlaced'),
                        h && lc?.wrongKind && t(line.kind === 'through' ? 'notice.joints.foundBlind' : 'notice.joints.foundThrough'),
                        h && lc?.reaches === false && line.aligned
                          && t('notice.joints.noReach').replace('{part}', name(line.aligned, r.side)),
                        ...notes.filter((n) => !!id && noteId(n) === id).map((n) => n.split(' · ').slice(1).join(' · ')),
                      ].filter((x): x is string => !!x);
                      const tag = <span className="font-medium">{`${id ? shownHoleId(id, makesOwnHole(items[Number(r.holes[0].for)])) : '?'} : `}</span>;
                      return said.length
                        ? said.map((m, j) => <div key={`${id}-${j}`} className={amber}>{tag}{m}</div>)
                        : [<div key={`${id}-ok`} className="whitespace-nowrap">{tag}<Check className="inline h-3.5 w-3.5 text-blue-600" /></div>];
                    })}
                  </td>
                </>
              ),
            }}
          />
          </div>
        </div>
      ) : !covers.length && (
        <p className="text-center text-sm text-slate-400">{t('notice.joints.noLines')}</p>
      )}
      {/* WHAT NO ROW HOLDS: notes about a hole no row shows, and about a part as a whole. */}
      {[...loose, ...partNotes].map((n) => <p key={n} className={`mt-1 text-center text-xs ${amber}`}>{n}</p>)}
      {/* THE COVERS, under the holes they go on: how many the rows use, how many were put down,
          and where one goes into a part. */}
      {covers.length > 0 && (
        <div className={`w-full overflow-x-auto ${check.lines.length ? 'mt-4' : ''}`}>
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={th}>{t('notice.joints.cover')}</th>
                <th className={th}>{t('notice.joints.coverExpected')}</th>
                <th className={th}>{t('notice.joints.coverPlaced')}</th>
              </tr>
            </thead>
            <tbody>
              {covers.map((c, i) => {
                const p = items[Number(c.id)];
                const said = [
                  c.placed != null && c.expected != null && c.placed !== c.expected
                    && t('notice.joints.foundOf').replace('{n}', String(c.placed)).replace('{m}', String(c.expected)),
                  ...c.into.map((o) => t('notice.joints.coverInto')
                    .replace('{part}', o.part).replace('{mm}', o.mm.toFixed(1))),
                ].filter(Boolean);
                const on = pickedCovers.has(c.id);
                return (
                  // THE ROW IS THE SWITCH, as the holes' are: on, its covers are outlined.
                  <tr
                    key={`${c.id}-${i}`}
                    onClick={() => onPickCover(c.id)}
                    aria-pressed={on}
                    className={`cursor-pointer transition-colors ${on
                      ? 'bg-blue-50'
                      : 'hover:bg-slate-50'}`}
                  >
                    <td className={td}>{withLetter(namedIn(p, language) || `#${c.id}`, p?.letter)}</td>
                    <td className={`${td} tabular-nums`}>{c.expected ?? '—'}</td>
                    <td className={`${td} tabular-nums`}>
                      {c.placed == null
                        ? <span className="text-slate-400">{t('notice.joints.coverUnplaced')}</span>
                        : said.length
                          // ONE LINE EACH, wrapping in the cell: four covers into a part, joined on one
                          // line, ran the table off its card.
                          ? (
                            <span className={`flex flex-col items-center gap-0.5 ${amber}`}>
                              {said.map((line, k) => <span key={k}>{line}</span>)}
                            </span>
                          )
                          : (
                            <span className="inline-flex items-center gap-1 text-blue-600">
                              <Check className="h-3.5 w-3.5" />
                              {c.placed}
                            </span>
                          )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {coverNotes && <p className={`mt-2 text-center text-sm ${amber}`}>{coverNotes}</p>}
        </div>
      )}
      {/* FOUND, AND NOT EXPECTED: a hole no line of the table accounts for. */}
      {check.extra.length > 0 && (
        <div className="mt-3 flex flex-col items-center gap-1 text-sm">
          <span className={`font-semibold ${amber}`}>{t('notice.joints.extra')}</span>
          {check.extra.map((h) => (
            <span key={h.id} className={amber}>
              {h.id} · {partLink(h.part, withLetter(namedIn(items[Number(h.part)], language) || `#${h.part}`, items[Number(h.part)]?.letter))}
              {h.via != null && ` ← ${namedIn(items[Number(h.via)], language) || `#${h.via}`}`}
            </span>
          ))}
        </div>
      )}
    </TableCard>
  );
}
